import Phaser from 'phaser';
import { LEVEL } from '../config';
import { GALAXY_1 } from '../galaxy/galaxies';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import {
  GAME_MODE,
  PRACTICE_EXERCISES,
  getGameMode,
  normalizePracticeExerciseCount,
  setSessionExerciseCount,
} from '../systems/GameMode';
import { formatScore } from '../util/numberFormat';
import { SUMA_MISSIONS, CHALLENGES_PER_MISSION } from '../planets/additionMissions';
import { RESTA_MISSIONS } from '../planets/subtractionMissions';
import { MULTIPLICACION_MISSIONS } from '../planets/multiplicationMissions';
import { DIVISION_MISSIONS } from '../planets/divisionMissions';
import { mountMenu, element, button, header, modeBadge, rows, go, primaryShortcut } from '../util/menuUI';

const QUICK_PRACTICE_COUNTS = [5, 10, 15, 20, 25, 30];

export class MissionScene extends Phaser.Scene {
  constructor() { super('MissionScene'); }

  create() {
    const root = mountMenu(this);
    const id = this.registry.get('currentPlanet') || 'suma';
    const planet = GALAXY_1.planets.find(p => p.id === id) || GALAXY_1.planets[0];
    const themeClass = id === 'resta' ? 'tmag-mission-resta' : id === 'mult' ? 'tmag-mission-mult' : id === 'division' ? 'tmag-mission-division' : 'tmag-mission-suma';
    root.classList.add(themeClass);
    if (id === 'resta') root.classList.add('tmag-resta');
    if (id === 'mult') root.classList.add('tmag-multiplicacion');
    if (id === 'division') root.classList.add('tmag-division');

    const missions = id === 'resta' ? RESTA_MISSIONS : id === 'mult' ? MULTIPLICACION_MISSIONS : id === 'division' ? DIVISION_MISSIONS : SUMA_MISSIONS;
    const mission = this.registry.get('currentMissionData') || missions[0];
    const missionMapScene = id === 'resta' ? 'RestaMissionsScene' : id === 'mult' ? 'MultiplicacionMissionsScene' : id === 'division' ? 'DivisionMissionsScene' : 'SumaMissionsScene';
    const isPractice = getGameMode(this.registry) === GAME_MODE.PRACTICE;

    const menuHeader = header(
      root,
      isPractice ? 'CONFIGURA TU PRÁCTICA' : `${planet.name} · ${mission.name}`,
      isPractice ? '' : mission.desc,
      '← SISTEMA',
      () => go(this, missionMapScene),
    );
    menuHeader.append(modeBadge(this));

    if (isPractice) {
      this.createPracticeSetup(root, planet, mission);
      return;
    }

    const panel = element('div', 'tmag-detail');
    panel.append(element('div', 'tmag-mission-number', mission.num));
    rows(panel, [
      ['Destreza', mission.skill], ['Retos matemáticos', CHALLENGES_PER_MISSION],
      ['Vidas', LEVEL.MAX_LIVES], ['Tiempo disponible', `${mission.time}s`],
      ['Meta de dominio', '80% de precisión (máximo 2 errores)'],
      ['Mejor puntuación', PlayerData.getMissionBest(planet.id, mission.index) ? formatScore(PlayerData.getMissionBest(planet.id, mission.index)) : '—'],
    ]);
    const start = () => go(this, planet.scene || 'LevelScene', () => this.registry.get('audio')?.playCollect());
    panel.append(button('▶ INICIAR MISIÓN', start, 'tmag-button tmag-primary'));
    root.append(panel);
    primaryShortcut(root, start);
  }

  createPracticeSetup(root, planet, mission) {
    const panel = element('div', 'tmag-detail tmag-practice-setup');
    panel.append(
      element('p', 'tmag-practice-kicker', `PLANETA ${mission.num}`),
      element('h2', 'tmag-practice-skill', mission.skill),
      element('p', 'tmag-practice-question', '¿Cuántos ejercicios quieres practicar?'),
    );

    const choices = element('div', 'tmag-practice-counts');
    const customRow = element('label', 'tmag-practice-custom');
    customRow.append(element('span', 'tmag-practice-custom-label', 'OTRO'));

    const customInput = element('input', 'tmag-practice-input');
    customInput.type = 'number';
    customInput.min = String(PRACTICE_EXERCISES.MIN);
    customInput.max = String(PRACTICE_EXERCISES.MAX);
    customInput.step = '1';
    customInput.inputMode = 'numeric';
    customInput.placeholder = '5–50';
    customInput.setAttribute('aria-label', 'Otra cantidad de ejercicios, entre 5 y 50');
    customRow.append(customInput);

    const note = element('p', 'tmag-practice-note', 'Entre 5 y 50 ejercicios.');
    const feedback = element('p', 'tmag-practice-feedback');
    feedback.setAttribute('aria-live', 'polite');

    const summary = element('div', 'tmag-practice-summary');
    const summarySkill = element('p', '', `Destreza: ${mission.skill}`);
    const summaryCount = element('p', '');
    summary.append(
      summarySkill,
      summaryCount,
      element('p', '', 'Objetivo: Practicar a tu ritmo'),
    );

    let selectedCount = PRACTICE_EXERCISES.DEFAULT;
    let usingCustom = false;
    const quickButtons = new Map();

    const updateSelection = () => {
      quickButtons.forEach((quickButton, count) => {
        quickButton.classList.toggle('is-selected', !usingCustom && count === selectedCount);
        quickButton.setAttribute('aria-pressed', String(!usingCustom && count === selectedCount));
      });

      const isValid = selectedCount !== null;
      customInput.classList.toggle('is-invalid', usingCustom && !isValid);
      feedback.textContent = usingCustom && !isValid
        ? 'Escribe un número entero entre 5 y 50.'
        : '';
      summaryCount.textContent = `Cantidad: ${isValid ? selectedCount : '—'} ejercicios`;
      startButton.disabled = !isValid;
      startButton.setAttribute('aria-disabled', String(!isValid));
    };

    QUICK_PRACTICE_COUNTS.forEach((count) => {
      const quickButton = button(String(count), () => {
        usingCustom = false;
        selectedCount = count;
        customInput.value = '';
        updateSelection();
      }, 'tmag-button tmag-practice-count');
      quickButton.setAttribute('aria-pressed', 'false');
      quickButtons.set(count, quickButton);
      choices.append(quickButton);
    });

    customInput.addEventListener('keydown', (event) => {
      if (['e', 'E', '+', '-'].includes(event.key)) event.preventDefault();
    });
    customInput.addEventListener('input', () => {
      usingCustom = true;
      selectedCount = normalizePracticeExerciseCount(customInput.value);
      updateSelection();
    });

    const startPractice = () => {
      const count = usingCustom
        ? normalizePracticeExerciseCount(customInput.value)
        : selectedCount;
      const savedCount = setSessionExerciseCount(this.registry, count);
      if (savedCount === null) {
        usingCustom = true;
        selectedCount = null;
        updateSelection();
        customInput.focus();
        return;
      }

      go(this, 'PracticeScene', () => {
        // Esta sesión no activa la práctica inteligente previa ni toca Competencia.
        this.registry.set('adaptivePractice', false);
        this.registry.get('audio')?.playCollect();
      });
    };

    const startButton = button('▶ COMENZAR PRÁCTICA', startPractice, 'tmag-button tmag-primary tmag-practice-start');
    panel.append(choices, customRow, note, feedback, summary, startButton);
    root.append(panel);
    updateSelection();
    primaryShortcut(root, startPractice);
  }
}
